export const STEMS = ['click', 'vocals', 'band', 'rhythm'] as const;
export type StemId = typeof STEMS[number];
export const STEM_LABELS: Record<StemId, string> = { click: 'Click & Guide', vocals: 'Vocals', band: 'Band', rhythm: 'Bass & Drums' };
export interface Section { name: string; start: number; end: number }
export interface StemInput { bytes?: ArrayBuffer; read?: () => Promise<ArrayBuffer>; name: string; expectedHash?: string; encodedSize?: number }
export type StemInputs = Partial<Record<StemId, StemInput>>;
export interface ChannelState { gain: number; muted: boolean; solo: boolean; status: 'empty' | 'loading' | 'ready' | 'unavailable'; filename?: string; error?: string; hash?: string; peak: number; metadata?: import('./metadata').AudioMetadata }
export interface Diagnostics { startTime: number; startSpreadFrames: number; loopPasses: number; loopPeriodFrames: number; decodedBytes: number; encodedBytes: number; bankBytes: number; ceilingDb: number; scheduler: string; interruptions: string[] }
export interface PlaybackSettings { semitones: number; rate: number }
export interface MixerSnapshot { status: 'empty' | 'loading' | 'partial' | 'ready' | 'playing' | 'paused' | 'error'; position: number; duration: number; sampleRate: number; loop: Section | null; channels: Record<StemId, ChannelState>; diagnostics: Diagnostics; playback: PlaybackSettings; pitchTempoSupported: boolean; error?: string }
export interface IMixerEngine {
  load(inputs: StemInputs): Promise<void>;
  play(): Promise<void>;
  pause(reason?: string): void;
  seek(seconds: number): Promise<void>;
  skip(seconds: number): Promise<void>;
  setGain(stem: StemId, linearGain: number): void;
  setMute(stem: StemId, muted: boolean): void;
  setSolo(stem: StemId, solo: boolean): void;
  setLoop(section: Section | null): Promise<void>;
  setPlaybackSettings(settings: PlaybackSettings): Promise<void>;
  snapshot(): MixerSnapshot;
  subscribe(listener: (state: MixerSnapshot) => void): () => void;
  dispose(): Promise<void>;
}

// Deliberately narrow structural port implemented by Web Audio and native Audio API.
export interface BufferPort { length: number; duration: number; sampleRate: number; numberOfChannels: number; getChannelData(channel: number): Float32Array }
// Buffered processors share one audio clock and source timeline.
export interface BufferedTransportPort {
  leadSeconds: number;
  load(buffers: Partial<Record<StemId, BufferPort>>): Promise<void>;
  start(when: number, offset: number, settings: PlaybackSettings, loop: Section | null): Promise<void>;
  stop(when: number): void;
  dispose(): void;
}
export interface ParamPort { value: number; setValueAtTime(value: number, time: number): void; linearRampToValueAtTime(value: number, time: number): void; cancelScheduledValues(time: number): void }
export interface NodePort { connect(destination: NodePort): unknown; disconnect(): void }
export interface GainPort extends NodePort { gain: ParamPort }
export interface SourcePort extends NodePort { buffer: BufferPort | null; loop: boolean; loopStart: number; loopEnd: number; start(when: number, offset?: number, duration?: number): void; stop(when?: number): void }
export interface GraphPort {
  currentTime: number; sampleRate: number; state: string; destination: NodePort;
  createBuffer(channels: number, length: number, rate: number): BufferPort;
  createBufferSource(): SourcePort;
  createGain(): GainPort;
  decodeAudioData(bytes: ArrayBuffer): Promise<BufferPort>;
  resume(): Promise<unknown>;
  close(): Promise<unknown>;
}
